/**
 * @documental
 * @export SubmitButton
 */
import { FC, PropsWithChildren, RefCallback } from "react";

import { SubmitGroup } from "capitalrx-components-2.0/lib/forms/SubmitGroup";
import { Button, ButtonProps } from "capitalrx-components-2.0/lib/general/Button";
import { Placeholder } from "capitalrx-components-2.0/lib/general/Button/Placeholder";
import { StyledButton } from "capitalrx-components-2.0/lib/general/Button/StyledButton";
import { getAppearanceForString } from "capitalrx-components-2.0/lib/general/Button/helpers";
import { useDisabled } from "capitalrx-components-2.0/lib/general/DisableGroup";
import { Icon } from "capitalrx-components-2.0/lib/general/Icon";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general/LoadingGroup";
import { Spinner } from "capitalrx-components-2.0/lib/general/Spinner";
import { Tooltip } from "capitalrx-components-2.0/lib/general/Tooltip";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Semibold } from "capitalrx-components-2.0/lib/typography";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib/utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "capitalrx-components-2.0/lib/utilities/withSubComponents";

import styles from "./styles.module.scss";

/**
 * The `SubmitButton` component properties.
 */

export interface SubmitButtonProps extends Omit<ButtonProps, "onClick" | "tabIndex"> {}

/**
 * The `SubmitButton` component sub components.
 */
export interface SubmitButtonSubComponents {
  /**
   * A placeholder component for the Button which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A form submit button.
 *
 * `import { SubmitButton } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>Example use case:</strong>
 *
 * `<SubmitButton {...props}>Submit</SubmitButton>;`
 */
export const SubmitButton = withSubComponents<FC<PropsWithChildren<SubmitButtonProps>>, SubmitButtonSubComponents>(
  {
    Placeholder,
  },
  (props) => {
    const {
      appearance = "primary",
      size = "large",
      icon,
      iconPosition = "left",
      children,
      tooltip,
      autoFocus = false,
      transparent = false,
      "aria-busy": ariaBusy,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedby,
    } = props;
    const iconOnly = !!(icon && !children);

    if (process.env.NODE_ENV !== "production" && iconOnly && !props["aria-label"]) {
      console.warn(
        `SubmitButton: Icon-only buttons should have an 'aria-label' for accessibility.\n`
          + `Button with icon "${icon}" is missing an aria-label.\n`
          + `Screen reader users won't know what this button does.\n`
          + `Example: <SubmitButton icon="${icon}" aria-label="Descriptive action text" />`,
      );
    }

    const disabled = useDisabled(props);

    const appearanceData = typeof appearance === "string" ? getAppearanceForString(appearance, transparent, size) : appearance;

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.SubmitButton,
        "submit-button",
        appearanceData.className,
        size,
        {
          transparent,
          "icon-only": iconOnly,
        },
      ],
      testIdPrefix: "submit-button",
    });

    const content = (ref?: RefCallback<HTMLButtonElement>) => (
      <StyledButton
        {...baseHtmlProps}
        ref={ref}
        appearance={appearanceData}
        disabled={disabled}
        iconOnly={iconOnly}
        type="submit"
        autoFocus={autoFocus}
        aria-busy={ariaBusy}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
      >
        <Shelf spacing="small">
          {icon && (iconPosition === "left" || iconOnly) && <Icon name={icon} size="regular" />}
          {!iconOnly && <Semibold>{children ?? <>&nbsp;</>}</Semibold>}
          {icon && iconPosition === "right" && !iconOnly && <Icon name={icon} size="regular" />}
        </Shelf>
      </StyledButton>
    );

    return (
      <LoadingGroup.Placeholder
        fallback={
          <Placeholder className={props.className} style={props.style} appearance={appearanceData} icon={!!icon}>
            {children}
          </Placeholder>
        }
      >
        <SubmitGroup.Placeholder
          fallback={
            <Button
              className={styles.SubmitPlaceholder}
              transparent={transparent}
              appearance={appearance}
              size={size}
              tooltip={tooltip}
              tabIndex={-1}
              aria-busy={ariaBusy}
              aria-describedby={ariaDescribedby}
              disabled
            >
              <Shelf spacing="small">
                <Spinner size="regular" />
                <span>Submitting</span>
              </Shelf>
            </Button>
          }
        >
          {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
          {!tooltip && content()}
        </SubmitGroup.Placeholder>
      </LoadingGroup.Placeholder>
    );
  },
);
